import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  FiFileText, FiArrowRight, FiAlertCircle, FiInbox, FiMessageSquare,
  FiInfo, FiTool, FiGrid, FiBarChart2, FiUsers,
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card, CardContent } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { Layout } from '../../components/layout/Layout';

interface SolicitacaoResumo {
  id: string;
  numero: string;
  titulo: string;
  status: string;
  data_abertura: string;
  modulo_codigo: string;
}

// Serviços abrangentes: canais amplos, abertos a qualquer cidadão, que NÃO
// dependem de um serviço/protocolo específico do catálogo.
const ABRANGENTES = [
  { titulo: 'Ouvidoria', descricao: 'Sugestões, reclamações, denúncias e elogios — pode ser anônima.', href: '/ouvidoria', icone: <FiMessageSquare size={20} />, exigeCadastro: false },
  { titulo: 'Acesso à informação', descricao: 'Pedidos de informação pública (LAI — Lei 12.527/2011).', href: '/esic', icone: <FiInfo size={20} />, exigeCadastro: true },
  { titulo: 'Zeladoria urbana', descricao: 'Buracos, iluminação, limpeza e problemas na cidade.', href: '/zeladoria', icone: <FiTool size={20} />, exigeCadastro: false },
];

export function HomePage() {
  const { cidadao, cadastroCompleto } = useAuth();
  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoResumo[]>([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (!cidadao) return;
    async function carregar() {
      const { data } = await supabase
        .from('documentos')
        .select('id, numero, titulo, status, data_abertura, modulo_codigo')
        .eq('solicitante_cidadao_id', cidadao!.id)
        .is('deletado_em', null)
        .order('data_ultima_movimentacao', { ascending: false })
        .limit(10);
      setSolicitacoes((data ?? []) as SolicitacaoResumo[]);
      setCarregando(false);
    }
    carregar();
  }, [cidadao]);

  const primeiroNome = (cidadao?.nome_social || cidadao?.nome || '').split(' ')[0];

  return (
    <Layout>
      {/* Hero */}
      <div className="bg-gradient-to-br from-institutional via-institutional-dark to-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <h1 className="text-2xl sm:text-3xl font-bold">Olá, {primeiroNome}</h1>
          <p className="text-blue-100/90 mt-1.5">Acompanhe suas solicitações e fale com a prefeitura.</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 -mt-6">
        {!cadastroCompleto && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="flex items-start gap-3">
              <FiAlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-amber-900">Complete seu cadastro</h3>
                <p className="text-sm text-amber-800 mt-1">
                  Adicione CPF, telefone e endereço para abrir solicitações.
                </p>
              </div>
              <Link
                to="/completar-cadastro"
                className="inline-flex items-center gap-1 text-sm font-semibold text-amber-900 hover:underline whitespace-nowrap"
              >
                Completar <FiArrowRight size={14} />
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Serviços abrangentes (ouvidoria, e-SIC, zeladoria) */}
        <div className="mb-3">
          <h2 className="text-sm uppercase tracking-wider text-slate-500 font-semibold">
            Serviços abrangentes
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Canais amplos, abertos a qualquer cidadão. Não dependem de um serviço
            específico do catálogo — use-os para se manifestar, pedir informação ou
            relatar um problema na cidade.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          {ABRANGENTES.map((s) => (
            <CardAcao
              key={s.titulo}
              titulo={s.titulo}
              descricao={s.descricao}
              href={s.href}
              icone={s.icone}
              disabled={s.exigeCadastro && !cadastroCompleto}
            />
          ))}
        </div>

        {/* Solicitações e protocolos do cidadão */}
        <div className="mb-3">
          <h2 className="text-sm uppercase tracking-wider text-slate-500 font-semibold">
            Solicitações e protocolos
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Pedidos formais à prefeitura a partir da Carta de Serviços, vinculados ao seu cadastro.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <CardAcao
            titulo="Abrir solicitação"
            descricao="Escolha um serviço da Carta de Serviços e abra um protocolo."
            href="/servicos"
            icone={<FiFileText size={20} />}
            disabled={!cadastroCompleto}
          />
          <CardAcao
            titulo="Enquetes"
            descricao="Participe das consultas públicas da prefeitura."
            href="/enquetes"
            icone={<FiBarChart2 size={20} />}
          />
          <CardAcao
            titulo="Conselho de usuários"
            descricao="Vagas, candidatura e conselheiros do serviço público (Lei 13.460/2017)."
            href="/conselho"
            icone={<FiUsers size={20} />}
          />
          <CardAcao
            titulo="Processos em que participo"
            descricao="Documentos que a prefeitura compartilhou com você para análise, resposta ou ciência."
            href="/participo"
            icone={<FiUsers size={20} />}
          />
        </div>

        {/* Recentes */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm uppercase tracking-wider text-slate-500 font-semibold">
            Minhas solicitações recentes
          </h2>
          {solicitacoes.length > 0 && (
            <Link to="/minhas-solicitacoes" className="text-sm text-institutional font-medium hover:underline inline-flex items-center gap-1">
              Ver todas <FiArrowRight size={14} />
            </Link>
          )}
        </div>

        {carregando ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : solicitacoes.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <FiInbox className="text-slate-400" size={26} />
              </div>
              <p className="text-sm font-medium text-slate-700">Você ainda não tem solicitações.</p>
              <p className="text-xs text-slate-500 mt-1">
                Quando abrir uma, ela aparecerá aqui para acompanhamento.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <ul className="divide-y divide-slate-100">
                {solicitacoes.map((s) => (
                  <li key={s.id}>
                    <Link to={`/solicitacao/${s.numero}`} className="flex items-center gap-4 p-4 hover:bg-slate-50 transition">
                      <div className="w-10 h-10 rounded-lg bg-institutional/10 text-institutional flex items-center justify-center flex-shrink-0">
                        <FiFileText size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{s.titulo}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{s.numero}</div>
                      </div>
                      <StatusBadge status={s.status} />
                      <FiArrowRight className="text-slate-300 flex-shrink-0" size={16} />
                    </Link>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}

function CardAcao({ titulo, descricao, href, icone, disabled, className }: {
  titulo: string; descricao: string; href: string; icone: ReactNode; disabled?: boolean; className?: string;
}) {
  const conteudo = (
    <Card className={`h-full transition ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-md hover:-translate-y-0.5 cursor-pointer'}`}>
      <CardContent>
        <div className="w-11 h-11 rounded-xl bg-institutional/10 text-institutional mb-3 flex items-center justify-center">
          {icone ?? <FiGrid size={20} />}
        </div>
        <h3 className="text-base font-semibold text-slate-900">{titulo}</h3>
        <p className="text-sm text-slate-600 mt-1">{descricao}</p>
        {disabled && <p className="text-xs text-amber-700 mt-2">Complete o cadastro para usar.</p>}
      </CardContent>
    </Card>
  );
  if (disabled) return <div className={className}>{conteudo}</div>;
  return <Link to={href} className={`block ${className ?? ''}`}>{conteudo}</Link>;
}

const STATUS_CFG: Record<string, { label: string; cls: string }> = {
  rascunho: { label: 'Rascunho', cls: 'bg-slate-100 text-slate-600' },
  aberto: { label: 'Aberto', cls: 'bg-blue-50 text-blue-700' },
  em_tramitacao: { label: 'Em tramitação', cls: 'bg-blue-50 text-blue-700' },
  aguardando_solicitante: { label: 'Aguardando você', cls: 'bg-amber-50 text-amber-700' },
  aguardando_aceite: { label: 'Aguardando aceite', cls: 'bg-amber-50 text-amber-700' },
  em_analise: { label: 'Em análise', cls: 'bg-blue-50 text-blue-700' },
  em_diligencia: { label: 'Em diligência', cls: 'bg-amber-50 text-amber-700' },
  deferido: { label: 'Deferido', cls: 'bg-emerald-50 text-emerald-700' },
  indeferido: { label: 'Indeferido', cls: 'bg-red-50 text-red-700' },
  arquivado: { label: 'Arquivado', cls: 'bg-slate-100 text-slate-600' },
  cancelado: { label: 'Cancelado', cls: 'bg-slate-100 text-slate-600' },
  concluido: { label: 'Concluído', cls: 'bg-emerald-50 text-emerald-700' },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CFG[status] ?? { label: status, cls: 'bg-slate-100 text-slate-600' };
  return (
    <span className={`hidden sm:inline-flex flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}
